/// Pure geometry helpers for the heading dial component.
///
/// Both functions are stateless and DOM-free so they can be unit-
/// tested without rendering. The component layer in HeadingDial.tsx
/// calls these from pointer-event handlers.

/// Convert a pointer position (in any coordinate space — typically
/// client-space from a PointerEvent or SVG-local from a
/// getBoundingClientRect-corrected event) to a compass bearing.
///
/// Returns a value in [0, 360):
///   N = 0, E = 90, S = 180, W = 270.
///
/// Internally uses atan2 (math convention: 0 = east, CCW positive),
/// then rotates to compass convention (0 = north, CW positive).
/// The +Y axis on screen points DOWN, which already gives us CW
/// rotation when we negate the conventional CCW direction.
export function pointerToBearing(
  pointerX: number,
  pointerY: number,
  centerX: number,
  centerY: number,
): number {
  const dx = pointerX - centerX
  const dy = pointerY - centerY
  // atan2 returns radians in (-PI, PI], with 0 along +X (east) and
  // positive going CCW. We want 0 along -Y (north) and positive going
  // CW. Compass = (90° - atan2°) mod 360.
  // Negate dy to convert screen coords (down = positive) to math
  // coords (up = positive) before feeding atan2.
  const mathDeg = (Math.atan2(-dy, dx) * 180) / Math.PI
  const bearing = (90 - mathDeg + 360) % 360
  // Guard against -0 and 360.0 due to FP rounding so the consumer
  // always sees a value in [0, 360).
  return bearing === 360 ? 0 : bearing
}

/// Snap a bearing to the nearest 5°, wrapping 357.5..360 back to 0.
/// Matches the spec's "snap to 5°" requirement and gives the bug
/// crisp click points around the rim without forcing pixel-perfect
/// drag accuracy.
export function snapToFive(bearingDeg: number): number {
  return (Math.round(bearingDeg / 5) * 5) % 360
}
